import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw, Scale } from 'lucide-react';

export default function FCContributionMargins({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data: d } = await api.get(`/v1/projects/${projectId}/fc/contribution-margins`); setData(d); }
    catch { /* ignore */ }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetch(); }, [fetch]);

  const recalculate = async () => {
    try { await api.post(`/v1/projects/${projectId}/fc/contribution-margins/recalculate`); toast.success('Margins recalculated'); fetch(); }
    catch (e) { toast.error(e?.response?.data?.detail || e?.message || 'Failed to recalculate margins'); }
  };

  const items = data?.items || [];
  const totalMH = data?.totalProjectManhours || 0;

  return (
    <div className="space-y-4" data-testid="fc-contribution-margins">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <Scale className="w-4 h-4" /> Contribution Margins (Manhour-Weighted)
          </h3>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Weightage = (Activity MH / Total Project MH) x 100
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={recalculate} data-testid="recalculate-margins-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Recalculate
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <p className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>Total Project Manhours</p>
          <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{totalMH.toLocaleString()}</p>
        </div>
        <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <p className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>Activities</p>
          <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{data?.activityCount || 0}</p>
        </div>
        <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <p className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>Sum Weightage</p>
          <p className="text-2xl font-bold" style={{ color: items.length ? '#10b981' : 'var(--text-muted)' }}>
            {items.reduce((s, i) => s + (i.contributionWeightagePercent || 0), 0).toFixed(2)}%
          </p>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
      ) : (
        <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
          <table className="w-full text-xs" data-testid="margins-table">
            <thead>
              <tr style={{ background: 'var(--card-bg-alt)' }}>
                <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Activity</th>
                <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Disc</th>
                <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Area</th>
                <th className="p-2 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Scope Qty</th>
                <th className="p-2 text-right font-medium" style={{ color: 'var(--text-muted)' }}>UOM</th>
                <th className="p-2 text-right font-medium" style={{ color: 'var(--text-muted)' }}>MH Factor</th>
                <th className="p-2 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Total MH</th>
                <th className="p-2 text-right font-medium" style={{ color: 'var(--accent-primary)' }}>Weightage %</th>
                <th className="p-2 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Progress %</th>
                <th className="p-2 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Weighted Progress</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                const wp = ((item.contributionWeightagePercent || 0) * (item.currentProgressPct || 0) / 100);
                return (
                  <tr key={idx} className="border-t" style={{ borderColor: 'var(--border-color)' }}>
                    <td className="p-2">
                      <span className="font-mono text-[10px]" style={{ color: 'var(--accent-primary)' }}>{item.activityId}</span>
                      <p className="truncate max-w-[180px]" style={{ color: 'var(--text-primary)' }}>{item.activityName}</p>
                    </td>
                    <td className="p-2" style={{ color: 'var(--text-muted)' }}>{item.disciplineCode}</td>
                    <td className="p-2" style={{ color: 'var(--text-muted)' }}>{item.area || '-'}</td>
                    <td className="p-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{item.scopeQuantity?.toLocaleString()}</td>
                    <td className="p-2 text-right" style={{ color: 'var(--text-muted)' }}>{item.uom}</td>
                    <td className="p-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{item.manhourFactor}</td>
                    <td className="p-2 text-right font-mono font-medium" style={{ color: 'var(--text-primary)' }}>{item.totalEquivalentManhours?.toLocaleString()}</td>
                    <td className="p-2 text-right">
                      <div className="flex items-center gap-1.5 justify-end">
                        <div className="w-12 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border-color)' }}>
                          <div className="h-full rounded-full" style={{ width: `${Math.min(item.contributionWeightagePercent || 0, 100)}%`, background: 'var(--accent-primary)' }} />
                        </div>
                        <span className="font-mono font-bold" style={{ color: 'var(--accent-primary)' }}>{item.contributionWeightagePercent?.toFixed(2)}%</span>
                      </div>
                    </td>
                    <td className="p-2 text-right font-mono" style={{ color: item.currentProgressPct > 0 ? '#10b981' : 'var(--text-muted)' }}>
                      {(item.currentProgressPct || 0).toFixed(1)}%
                    </td>
                    <td className="p-2 text-right font-mono font-medium" style={{ color: wp > 0 ? '#10b981' : 'var(--text-muted)' }}>
                      {wp.toFixed(4)}%
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr><td colSpan={10} className="p-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>No activities to display.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
